import { useState, useEffect } from 'react';
import { Button } from '@habitech/call-to-action';
import { TextField } from '@habitech/keychain-atoms-inputs';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import {
    selectDataSuggested,
    selectUserProfile,
    selectSuggestedUserId,
} from 'src/redux/shopping_cart/shopping_cart.selectors';
import { updateUserProfile } from 'src/redux/shopping_cart/shopping_cart.slice';
import { selectUser } from 'src/redux/user/userSlice';
import { updateProfile } from 'src/apis/updateProfileApi';
import Icon from 'src/components/common/Icon';
import ContactIcon from '../../UserInformation/UserInfoIcons';
import { ProfileNoticeAlert } from '../styles';
import {
    FieldGroup,
    FieldLabel,
    FieldLabelRow,
    FieldsGrid,
    ModalActions,
    ModalRoot,
    ModalTitle,
    NoticeWrapper,
    OtpBadge,
    ReadOnlyField,
    SaveError,
} from './UserProfileEditModal.styles';

const PHONE_LENGTH = 10;
const ONLY_NUMBERS_REGEX = /^\d*$/;
const ERROR_ONLY_NUMBERS = 'Solo se permiten números';
const ERROR_LENGTH = 'Debe tener 10 dígitos';
const ERROR_REQUIRED = 'Este campo es obligatorio';
const OTP_REVERIFY_SUFFIX =
    'Si lo editas, el usuario deberá volver a completar la verificación por OTP en su perfil.';

/**
 * Modal "Editar contacto básico".
 * Campos editables: Teléfono de contacto y WhatsApp (siempre), y Nombre/Apellido
 * únicamente cuando vienen vacíos desde el servicio.
 * El resto (ID, Email contacto, Email de la cuenta, Nombre Feed, Tipo de broker)
 * se muestra como solo lectura. El servicio `updateProfile` solo persiste
 * name/lastname/phone_contact/phone_whatsapp.
 */
interface UserProfileEditModalProps {
    onClose: () => void;
}

/**
 * Distintivo del estado de verificación de un canal: verde con "OTP" cuando está
 * verificado, rojo con "Sin validar" cuando no lo está.
 */
const OtpTag = ({ verified }: { verified: boolean }) => (
    <OtpBadge $verified={verified}>
        <ContactIcon name={verified ? 'check' : 'close'} size={13} />
        {verified ? 'OTP' : 'Sin validar'}
    </OtpBadge>
);

/**
 * Error de un campo de teléfono. El teléfono siempre es obligatorio: si queda
 * vacío (ya sea porque vino vacío o porque el comercial lo borró) muestra
 * "obligatorio"; en cualquier otro caso debe tener 10 dígitos.
 */
const getPhoneError = (value: string): string | null => {
    if (value.trim() === '') return ERROR_REQUIRED;
    if (!ONLY_NUMBERS_REGEX.test(value)) return ERROR_ONLY_NUMBERS;
    if (value.length !== PHONE_LENGTH) return ERROR_LENGTH;
    return null;
};

const UserProfileEditModal = ({ onClose }: UserProfileEditModalProps) => {
    const dispatch = useAppDispatch();
    const userProfile = useAppSelector(selectUserProfile);
    const userId = useAppSelector(selectSuggestedUserId);
    const { suggested } = useAppSelector(selectDataSuggested);
    const { token } = useAppSelector(selectUser);

    const nameIsEmpty = !userProfile?.name?.trim();
    const lastnameIsEmpty = !userProfile?.lastname?.trim();

    // Canales verificados por OTP. Si el gestor edita un número verificado,
    // el usuario tendrá que rehacer la verificación por OTP.
    const otp = userProfile?.otp_channel_verifications;
    const emailContactVerified = !!otp?.email_contact?.verified;
    const accountEmailVerified = !!otp?.personal_email?.verified;
    const phoneContactVerified = !!otp?.sms?.verified;
    const phoneWhatsappVerified = !!otp?.whatsapp?.verified;

    // Datos de solo lectura.
    const accountEmail = suggested.users.email || '-';
    const emailContactValue = otp?.email_contact?.value || '-';
    const feedName = userProfile?.feed_name?.trim() || '';
    const hasFeed = feedName !== '';
    const brokerType = userProfile?.broker_type || '-';

    // Aviso de qué canal está verificado por OTP. Si ambos lo están,
    // el mensaje los menciona como "ambos".
    let otpNotice = '';
    if (phoneContactVerified && phoneWhatsappVerified) {
        otpNotice = `Ambos números (teléfono de contacto y teléfono WhatsApp) están verificados por OTP. ${OTP_REVERIFY_SUFFIX}`;
    } else if (phoneContactVerified) {
        otpNotice = `El teléfono de contacto está verificado por OTP. ${OTP_REVERIFY_SUFFIX}`;
    } else if (phoneWhatsappVerified) {
        otpNotice = `El teléfono WhatsApp está verificado por OTP. ${OTP_REVERIFY_SUFFIX}`;
    }

    const [name, setName] = useState('');
    const [lastname, setLastname] = useState('');
    const [phoneContact, setPhoneContact] = useState(userProfile?.phone_contact ?? '');
    const [phoneWhatsapp, setPhoneWhatsapp] = useState(userProfile?.phone_whatsapp ?? '');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        setPhoneContact(userProfile?.phone_contact ?? '');
        setPhoneWhatsapp(userProfile?.phone_whatsapp ?? '');
    }, [userProfile?.phone_contact, userProfile?.phone_whatsapp]);

    const handlePhoneChange = (field: 'phone_contact' | 'phone_whatsapp', value: string) => {
        const numericValue = (value ?? '').replace(/\D/g, '').slice(0, PHONE_LENGTH);
        if (field === 'phone_contact') {
            setPhoneContact(numericValue);
        } else {
            setPhoneWhatsapp(numericValue);
        }
    };

    const handleNameChange = (value: string) => setName(value ?? '');
    const handleLastnameChange = (value: string) => setLastname(value ?? '');

    // Errores derivados del estado actual, para que el gestor vea de inmediato
    // qué le falta para habilitar Guardar: nombre/apellido son obligatorios solo
    // cuando vienen vacíos del servicio; los teléfonos son siempre obligatorios.
    const nameError = nameIsEmpty && name.trim() === '' ? ERROR_REQUIRED : null;
    const lastnameError = lastnameIsEmpty && lastname.trim() === '' ? ERROR_REQUIRED : null;
    const phoneContactError = getPhoneError(phoneContact);
    const phoneWhatsappError = getPhoneError(phoneWhatsapp);

    const isValid =
        phoneContact.length === PHONE_LENGTH &&
        phoneWhatsapp.length === PHONE_LENGTH &&
        !phoneContactError &&
        !phoneWhatsappError &&
        !nameError &&
        !lastnameError;

    const handleSaveProfile = async () => {
        if (!token) {
            setSaveError('No hay sesión activa');
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            const payload = {
                phone_contact: phoneContact,
                phone_whatsapp: phoneWhatsapp,
                update_for_customer: true,
                ...(userId ? { user_id_for_update: userId } : {}),
                ...(nameIsEmpty ? { name: name.trim() } : {}),
                ...(lastnameIsEmpty ? { lastname: lastname.trim() } : {}),
            };
            const res = await updateProfile(payload, token);
            if (res?.success) {
                dispatch(updateUserProfile({
                    phone_contact: phoneContact,
                    phone_whatsapp: phoneWhatsapp,
                    ...(nameIsEmpty ? { name: name.trim() } : {}),
                    ...(lastnameIsEmpty ? { lastname: lastname.trim() } : {}),
                }));
                onClose();
            } else {
                setSaveError(res?.message ?? 'Error al guardar el perfil');
            }
        } catch {
            setSaveError('Error de conexión al guardar el perfil');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) return;
        handleSaveProfile();
    };

    return (
        <ModalRoot>
            <ModalTitle>Editar contacto básico</ModalTitle>
            <form onSubmit={handleSubmit}>
                {otpNotice && (
                    <NoticeWrapper>
                        <ProfileNoticeAlert>
                            <Icon icon="pcom-icon-warning-filled" size={16} />
                            {otpNotice}
                        </ProfileNoticeAlert>
                    </NoticeWrapper>
                )}

                <FieldsGrid>
                    {/* ID (solo lectura) */}
                    <FieldGroup $full>
                        <FieldLabelRow>
                            <FieldLabel>
                                ID <span className="no-editable">(no editable)</span>
                            </FieldLabel>
                        </FieldLabelRow>
                        <ReadOnlyField>#{userId}</ReadOnlyField>
                    </FieldGroup>

                    {/* Nombre */}
                    <FieldGroup>
                        <FieldLabelRow>
                            <FieldLabel>
                                Nombre
                                {!nameIsEmpty && <span className="no-editable">(no editable)</span>}
                            </FieldLabel>
                        </FieldLabelRow>
                        {nameIsEmpty ? (
                            <TextField
                                originType="pcom"
                                name="name"
                                dataId="modal-name"
                                placeHolder="Nombre"
                                value={name}
                                onChange={(_, value) => handleNameChange(value ?? '')}
                                error={!!nameError}
                                errorMessage={nameError ?? ''}
                            />
                        ) : (
                            <ReadOnlyField>{userProfile?.name}</ReadOnlyField>
                        )}
                    </FieldGroup>

                    {/* Apellido */}
                    <FieldGroup>
                        <FieldLabelRow>
                            <FieldLabel>
                                Apellido
                                {!lastnameIsEmpty && <span className="no-editable">(no editable)</span>}
                            </FieldLabel>
                        </FieldLabelRow>
                        {lastnameIsEmpty ? (
                            <TextField
                                originType="pcom"
                                name="lastname"
                                dataId="modal-lastname"
                                placeHolder="Apellido"
                                value={lastname}
                                onChange={(_, value) => handleLastnameChange(value ?? '')}
                                error={!!lastnameError}
                                errorMessage={lastnameError ?? ''}
                            />
                        ) : (
                            <ReadOnlyField>{userProfile?.lastname}</ReadOnlyField>
                        )}
                    </FieldGroup>

                    {/* Email contacto (solo lectura) */}
                    <FieldGroup $full>
                        <FieldLabelRow>
                            <FieldLabel>
                                Email contacto <span className="no-editable">(no editable)</span>
                            </FieldLabel>
                            <OtpTag verified={emailContactVerified} />
                        </FieldLabelRow>
                        <ReadOnlyField>{emailContactValue}</ReadOnlyField>
                    </FieldGroup>

                    {/* Teléfono de contacto */}
                    <FieldGroup>
                        <FieldLabelRow>
                            <FieldLabel>Tel contacto</FieldLabel>
                            <OtpTag verified={phoneContactVerified} />
                        </FieldLabelRow>
                        <TextField
                            originType="pcom"
                            name="phone_contact"
                            dataId="modal-phone-contact"
                            placeHolder="Teléfono de contacto"
                            value={phoneContact}
                            onChange={(_, value) => handlePhoneChange('phone_contact', value ?? '')}
                            error={!!phoneContactError}
                            errorMessage={phoneContactError ?? ''}
                        />
                    </FieldGroup>

                    {/* WhatsApp */}
                    <FieldGroup>
                        <FieldLabelRow>
                            <FieldLabel>WhatsApp</FieldLabel>
                            <OtpTag verified={phoneWhatsappVerified} />
                        </FieldLabelRow>
                        <TextField
                            originType="pcom"
                            name="phone_whatsapp"
                            dataId="modal-phone-whatsapp"
                            placeHolder="Teléfono WhatsApp"
                            value={phoneWhatsapp}
                            onChange={(_, value) => handlePhoneChange('phone_whatsapp', value ?? '')}
                            error={!!phoneWhatsappError}
                            errorMessage={phoneWhatsappError ?? ''}
                        />
                    </FieldGroup>

                    {/* Email de la cuenta (solo lectura) */}
                    <FieldGroup $full>
                        <FieldLabelRow>
                            <FieldLabel>
                                Email de la cuenta <span className="no-editable">(no editable)</span>
                            </FieldLabel>
                            <OtpTag verified={accountEmailVerified} />
                        </FieldLabelRow>
                        <ReadOnlyField>{accountEmail}</ReadOnlyField>
                    </FieldGroup>

                    {/* Nombre Feed (solo lectura). Solo se muestra cuando hay feed/CRM. */}
                    {hasFeed && (
                        <FieldGroup>
                            <FieldLabelRow>
                                <FieldLabel>
                                    Nombre Feed <span className="no-editable">(no editable)</span>
                                </FieldLabel>
                            </FieldLabelRow>
                            <ReadOnlyField>{feedName}</ReadOnlyField>
                        </FieldGroup>
                    )}

                    {/* Tipo de broker (solo lectura) */}
                    <FieldGroup>
                        <FieldLabelRow>
                            <FieldLabel>
                                Tipo de broker <span className="no-editable">(no editable)</span>
                            </FieldLabel>
                        </FieldLabelRow>
                        <ReadOnlyField>{brokerType}</ReadOnlyField>
                    </FieldGroup>

                    {saveError && <SaveError>{saveError}</SaveError>}
                </FieldsGrid>

                <ModalActions>
                    <Button
                        type="button"
                        originType="pcom"
                        variant="stroke"
                        size="large"
                        dataId="button-cancel-modal"
                        onClick={onClose}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        originType="pcom"
                        variant="primary"
                        size="large"
                        dataId="button-send-modal"
                        disabled={!isValid || saving}
                    >
                        {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </ModalActions>
            </form>
        </ModalRoot>
    );
};

export default UserProfileEditModal;
